/**
 * Every conversation's live state, kept above the chat pane (#193).
 *
 * The pane used to hold exactly one turn — one transcript, one `busy`, one pending
 * approval — and that turn's idea of its own thread followed whatever was on
 * screen. So a running turn kept streaming after the user opened another thread,
 * another project or a new chat, but nothing it produced could find its way home:
 * its tokens were dropped, its approval card landed on the thread now shown (and
 * approving it resumed *that* thread), and coming back showed no sign it was
 * running.
 *
 * Here each conversation is a **session** with its own state, its own abort
 * controller and its own pinned turn settings. A turn writes only into the session
 * it started in — the closures hold the session object, never "the current
 * thread" — and the pane renders whichever session is open. Leaving and coming
 * back is just rendering a different session; nothing has to be reattached.
 *
 * Sessions are keyed by thread id, or by a draft key for a chat that has not
 * minted its thread yet. The agent sends the id mid-stream (`thread_info`), and
 * `adopt` re-keys the session under it, so a running new chat can be found from
 * History afterwards like any other thread.
 *
 * Plain state with a subscribe, not React: a turn outlives any render, and the
 * rules here are testable without one.
 */
import type { HostKind } from "./hostStatus";
import type { ChatError, ChatMessage, ActivePlan } from "../hooks/useChatTurn";
import type { ContextUsage, InterruptPayload } from "./chat";

export interface ChatSessionState {
  messages: ChatMessage[];
  /** A turn is in flight. */
  busy: boolean;
  /** Waiting on the user to approve or refuse an action. */
  interrupt: InterruptPayload | null;
  usage: ContextUsage | null;
  error: ChatError | null;
  activePlan: ActivePlan | null;
}

export const EMPTY_SESSION_STATE: ChatSessionState = Object.freeze({
  messages: [],
  busy: false,
  interrupt: null,
  usage: null,
  error: null,
  activePlan: null,
}) as ChatSessionState;

/**
 * What a turn was started with, kept for its resume.
 *
 * A resume has to re-init the agent in the same mode, at the same effort, against
 * the same instances the interrupted turn used — the live selectors will not do:
 * change one while an approval is pending and the approved write lands somewhere
 * the user did not approve it for.
 */
export interface PinnedTurn {
  mode: string;
  effort: string | undefined;
  instance: string | null;
  sessions: Partial<Record<HostKind, string>>;
  /** The workflow whose routes the turn went to, or null for chat: a resume must go where the interrupted turn went. */
  workflowId: string | null;
}

export interface ChatSession {
  /** Thread id, or a draft key until the agent mints one. */
  key: string;
  threadId: string | null;
  /** The project the conversation belongs to, once a turn has run in it. */
  projectId: string | null;
  state: ChatSessionState;
  /** The running turn's fetch; null when idle. */
  controller: AbortController | null;
  /**
   * A turn has run in this session during this app session, so its transcript is
   * fresher than the thread's saved history (which keeps text only, and nothing
   * of a turn still running). Opening the thread shows this, not a refetch.
   */
  ran: boolean;
  pinned: PinnedTurn | null;
}

let drafts = 0;
/** A key for a conversation with no thread yet. */
export function newDraftKey(): string {
  drafts += 1;
  return `draft:${drafts}`;
}

export class ChatSessions {
  private readonly byKey = new Map<string, ChatSession>();
  private readonly listeners = new Set<() => void>();
  private versionCounter = 0;

  /** Bumped on every change — the snapshot `useSyncExternalStore` compares. */
  get version(): number {
    return this.versionCounter;
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /** The session under this key, if there is one. Never creates — safe during render. */
  peek(key: string): ChatSession | undefined {
    return this.byKey.get(key);
  }

  /** The session under this key, created empty if there is none. */
  session(key: string): ChatSession {
    let session = this.byKey.get(key);
    if (!session) {
      session = {
        key,
        threadId: key.startsWith("draft:") ? null : key,
        projectId: null,
        state: EMPTY_SESSION_STATE,
        controller: null,
        ran: false,
        pinned: null,
      };
      this.byKey.set(key, session);
    }
    return session;
  }

  /** Replace a session's state. The session object is the address, so a turn's
   *  writes follow it wherever it is keyed now. */
  update(session: ChatSession, change: (state: ChatSessionState) => ChatSessionState): void {
    session.state = change(session.state);
    this.notify();
  }

  /** Mark a session's turn started or ended, and tell subscribers. */
  setController(session: ChatSession, controller: AbortController | null): void {
    session.controller = controller;
    this.notify();
  }

  /**
   * The agent minted this draft's thread. Re-key it, so the conversation is found
   * under its thread id from now on — from History, from another project, anywhere.
   */
  adopt(session: ChatSession, threadId: string): void {
    if (session.threadId === threadId && session.key === threadId) return;
    if (this.byKey.get(session.key) === session) this.byKey.delete(session.key);
    session.key = threadId;
    session.threadId = threadId;
    this.byKey.set(threadId, session);
    this.notify();
  }

  /**
   * A running conversation, other than `except`, that is driving any of these CAD
   * instances. One instance runs one command at a time and refuses the next, so a
   * second chat on it would fail mid-turn; the composer says so before sending.
   */
  instanceHolder(sessionIds: readonly string[], except: ChatSession | undefined): ChatSession | null {
    if (sessionIds.length === 0) return null;
    for (const session of this.byKey.values()) {
      if (session === except || !session.state.busy || !session.pinned) continue;
      const held = Object.values(session.pinned.sessions);
      if (sessionIds.some((id) => held.includes(id))) return session;
    }
    return null;
  }

  /**
   * The conversations still working or waiting on the user, oldest first — the
   * sidebar's way back to them from any tab or project (#193). Only those with a
   * thread: a new chat gets its id within the first second, and until then it has
   * nowhere to be opened from.
   */
  active(): ChatSession[] {
    return [...this.byKey.values()].filter(
      (session) => session.threadId !== null && (session.state.busy || session.state.interrupt !== null),
    );
  }

  /** Stop every running turn — the shell is going away (sign-out, org gate). */
  abortAll(): void {
    for (const session of this.byKey.values()) session.controller?.abort();
  }

  private notify(): void {
    this.versionCounter += 1;
    for (const listener of this.listeners) listener();
  }
}
